import React, { useMemo, useState } from "react";
import { Modal, Form, Select, Button, Statistic, Row, Col, Card, Radio, Divider, Alert } from "antd";
import { DownloadOutlined, FileExcelOutlined, MailOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  buildSegmentQuery,
  filterCustomers,
  getExportStats,
  cleanCustomersForExport,
  exportCustomersToCSV,
  exportCustomersToExcel,
  exportCustomersToMailchimp,
} from "utils/segmentExport";

const { Option } = Select;

const SegmentExportModal = ({ visible, onClose, customers, segments }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [exportFormat, setExportFormat] = useState("excel");
  const [filterValues, setFilterValues] = useState({ shopSource: "all" });

  // Get filtered customers based on form values
  const filteredCustomers = useMemo(() => {
    const query = buildSegmentQuery(filterValues);
    const filtered = filterCustomers(customers, query);
    return cleanCustomersForExport(filtered);
  }, [customers, filterValues]);

  // Get export statistics
  const stats = useMemo(() => {
    return getExportStats(filteredCustomers);
  }, [filteredCustomers]);

  const handleExport = () => {
    const segmentName = filterValues.segment || "alle_klanten";

    if (filteredCustomers.length === 0) {
      Modal.warning({
        title: t("no_customers_to_export"),
        content: t("please_adjust_filters"),
      });
      return;
    }

    if (exportFormat === "excel") {
      exportCustomersToExcel(filteredCustomers, segmentName);
    } else if (exportFormat === "csv") {
      exportCustomersToCSV(filteredCustomers, segmentName);
    } else if (exportFormat === "mailchimp") {
      exportCustomersToMailchimp(filteredCustomers, segmentName);
    }

    Modal.success({
      title: t("export_successful"),
      content: t("exported_customers_count", { count: filteredCustomers.length }),
    });

    onClose();
  };

  return (
    <Modal
      title={t("export_customer_segment")}
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("cancel")}
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={exportFormat === "mailchimp" ? <MailOutlined /> : <DownloadOutlined />}
          onClick={handleExport}
          disabled={filteredCustomers.length === 0}
        >
          {t("export")} ({filteredCustomers.length})
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        {/* Export Format */}
        <Form.Item label={t("export_format")}>
          <Radio.Group value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
            <Radio.Button value="excel">
              <FileExcelOutlined /> Excel
            </Radio.Button>
            <Radio.Button value="csv">
              <DownloadOutlined /> CSV
            </Radio.Button>
            <Radio.Button value="mailchimp">
              <MailOutlined /> Mailchimp
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Divider>{t("filters")}</Divider>

        {/* Segment Filter */}
        <Form.Item label={t("segment")}>
          <Select
            placeholder={t("all_segments")}
            allowClear
            value={filterValues.segment}
            onChange={(value) => setFilterValues((prev) => ({ ...prev, segment: value }))}
          >
            {segments.map((segment) => (
              <Option key={segment} value={segment}>
                {segment}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Shop Source Filter */}
        <Form.Item label={t("shop_source")}>
          <Select
            value={filterValues.shopSource}
            onChange={(value) => setFilterValues((prev) => ({ ...prev, shopSource: value }))}
          >
            <Option value="all">{t("all")}</Option>
            <Option value="VCE">VCE</Option>
            <Option value="HVO">HVO</Option>
          </Select>
        </Form.Item>

        <Divider>{t("export_preview")}</Divider>

        {/* Export Statistics */}
        <Card>
          <Row gutter={16}>
            <Col span={24}>
              <Statistic title={t("customers_to_export")} value={stats.totalCustomers} />
            </Col>
          </Row>

          {/* Shop Breakdown */}
          {Object.keys(stats.shopBreakdown).length > 0 && (
            <>
              <Divider />
              <div>
                <strong>{t("shop_breakdown")}:</strong>
                <div style={{ marginTop: 8 }}>
                  {Object.entries(stats.shopBreakdown).map(([shop, count]) => (
                    <div key={shop}>
                      {shop}: {count} {t("customers")}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Segment Breakdown */}
          {Object.keys(stats.segmentBreakdown).length > 1 && (
            <>
              <Divider />
              <div>
                <strong>{t("segment_breakdown")}:</strong>
                <div style={{ marginTop: 8 }}>
                  {Object.entries(stats.segmentBreakdown).map(([segment, count]) => (
                    <div key={segment}>
                      {segment}: {count} {t("customers")}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Excel Instructions */}
        {exportFormat === "excel" && (
          <Alert
            message={t("excel_export_instructions") || "Excel export"}
            description={
              t("excel_export_description") ||
              "Elk klantsegment komt op een eigen tabblad, met naam, e-mailadres, telefoonnummer."
            }
            type="info"
            style={{ marginTop: 16 }}
          />
        )}

        {/* Mailchimp Instructions */}
        {exportFormat === "mailchimp" && (
          <Alert
            message={t("mailchimp_export_instructions")}
            description={
              <div>
                <p>{t("mailchimp_csv_format_description")}</p>
                <ol>
                  <li>{t("mailchimp_step_1")}</li>
                  <li>{t("mailchimp_step_2")}</li>
                  <li>{t("mailchimp_step_3")}</li>
                </ol>
              </div>
            }
            type="info"
            style={{ marginTop: 16 }}
          />
        )}
      </Form>
    </Modal>
  );
};

export default SegmentExportModal;
